import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import {
  addIElements,
  selectRootId,
  useNonExhaustiveEffect,
} from "@faro-lotv/app-component-toolbox";
import {
  IElementGenericPointCloudStream,
  IElementType,
  isValid,
} from "@faro-lotv/ielement-types";
import {
  CaptureTreePointCloudType,
  RevisionScanEntity,
} from "@faro-lotv/service-wires";
import { useMemo } from "react";

/**
 *
 * @param scanEntities The scans in the revision to load the point cloud streams for.
 * @returns The point cloud streams corresponding to the given revision entities.
 *   If the data is still loading, `undefined` is returned.
 *   Note that some scans may not yet have a processed point cloud stream available.
 */
export function useLoadRevisionPointCloudStreams(
  scanEntities: RevisionScanEntity[],
): IElementGenericPointCloudStream[] | undefined {
  const rootId = useAppSelector(selectRootId);

  // Created mocked point cloud stream elements
  // This allows integration with the PointCloudObject-based functionality,
  // without having to do additional requests to load IElements
  const pointCloudStreams = useMemo(
    () =>
      scanEntities
        .map((scanEntity) => {
          const poTree = scanEntity.pointClouds?.find(
            (pointCloud) =>
              pointCloud.type === CaptureTreePointCloudType.poTree,
          );

          if (!poTree) return null;

          return {
            id: poTree.id,
            name: scanEntity.name,
            type: IElementType.pointCloudStream,
            createdAt: poTree.createdAt ?? scanEntity.createdAt,
            createdBy: scanEntity.createdBy,
            modifiedAt: poTree.createdAt ?? scanEntity.lastPatchedAt,
            modifiedBy: scanEntity.lastPatchedBy,
            // Critical, as this is how the matching point cloud is detected
            parentId: scanEntity.id,
            pose: {
              ...scanEntity.pose,
              isWorldRot: false,
              gps: null,
            },
            uri: poTree.uri ?? "",
            signedUri: poTree.signedUri ?? undefined,
            signedUriExpiresOn: poTree.signedUriExpiresOn ?? undefined,
            // Not ideal to fall back to empty string, but currently the rootId is not used
            rootId: rootId ?? "",
            root_Id: rootId ?? "",
          } satisfies IElementGenericPointCloudStream;
        })
        .filter(isValid),
    [scanEntities, rootId],
  );

  const dispatch = useAppDispatch();

  // Add the mocked point cloud streams to the store
  // Makes them accessible to other components
  useNonExhaustiveEffect(() => {
    dispatch(addIElements(pointCloudStreams));
  }, [pointCloudStreams]);

  return pointCloudStreams;
}
